/**
 * Журнал SMS и звонков дилерам о старте торгов (консоль «Сбер → журнал»).
 *
 * SMS: public.sms_outbox с source = 'auction_start' (пишет edge send-sms или
 * триггер строкой skipped). Звонки: public.call_outbox (очередь ставит
 * триггер, результат пишет робот), аудио в приватном bucket call-audio.
 * Миграции stryzh 20261103160000 и 20261105220000.
 */
import { createClient } from '@/api/supabase'

export type AuctionNotifyKind = 'sms' | 'call'

export type AuctionSmsStatus = 'sent' | 'failed' | 'skipped'
export type AuctionCallStatus =
  | 'queued'
  | 'calling'
  | 'answered'
  | 'no_answer'
  | 'busy'
  | 'rejected'
  | 'failed'
  | 'skipped'
  | 'expired'

export interface AuctionNotifyLead {
  id: string
  vehicle_brand: string | null
  vehicle_model: string | null
  vehicle_year: number | null
}

export interface AuctionNotifyRow {
  id: string
  kind: AuctionNotifyKind
  /** SMS: время отправки; звонок: начало звонка, пока не звонили: постановка в очередь. */
  at: string
  /** Цифры без знаков. */
  phone: string
  message: string
  status: AuctionSmsStatus | AuctionCallStatus
  detail: string | null
  lead: AuctionNotifyLead | null
  /** Только звонок: раньше этого времени не звонить (окно звонков). */
  notBefore: string | null
  /** Только звонок: длительность разговора, секунды. */
  durationSec: number | null
  /** Только звонок: код SIP финального ответа. */
  sipCode: number | null
  /** Только звонок: путь аудио в bucket call-audio. */
  audioPath: string | null
}

const LEAD_EMBED = 'inspection_leads(id, vehicle_brand, vehicle_model, vehicle_year)'

interface SmsRow {
  id: string
  phone: string
  message: string
  status: AuctionSmsStatus
  smstraffic_description: string | null
  sent_at: string
  inspection_leads: AuctionNotifyLead | null
}

interface CallRow {
  id: string
  phone: string
  message: string
  status: AuctionCallStatus
  result_detail: string | null
  not_before: string
  started_at: string | null
  created_at: string
  duration_sec: number | null
  sip_code: number | null
  audio_path: string | null
  inspection_leads: AuctionNotifyLead | null
}

/** Последние записи журнала, новые сверху. kind не задан: SMS и звонки вместе. */
export async function listAuctionNotifyLog(opts: { kind?: AuctionNotifyKind; limit: number }): Promise<AuctionNotifyRow[]> {
  const supabase = createClient()
  const wantSms = opts.kind !== 'call'
  const wantCalls = opts.kind !== 'sms'

  const [smsRes, callRes] = await Promise.all([
    wantSms
      ? supabase
          .from('sms_outbox')
          .select(`id, phone, message, status, smstraffic_description, sent_at, ${LEAD_EMBED}`)
          .eq('source', 'auction_start')
          .order('sent_at', { ascending: false })
          .limit(opts.limit)
      : null,
    wantCalls
      ? supabase
          .from('call_outbox')
          .select(
            `id, phone, message, status, result_detail, not_before, started_at, created_at, duration_sec, sip_code, audio_path, ${LEAD_EMBED}`,
          )
          .order('created_at', { ascending: false })
          .limit(opts.limit)
      : null,
  ])
  if (smsRes?.error) throw new Error(smsRes.error.message)
  if (callRes?.error) throw new Error(callRes.error.message)

  const sms: AuctionNotifyRow[] = ((smsRes?.data ?? []) as unknown as SmsRow[]).map((r) => ({
    id: r.id,
    kind: 'sms',
    at: r.sent_at,
    phone: r.phone,
    message: r.message,
    status: r.status,
    detail: r.smstraffic_description,
    lead: r.inspection_leads,
    notBefore: null,
    durationSec: null,
    sipCode: null,
    audioPath: null,
  }))
  const calls: AuctionNotifyRow[] = ((callRes?.data ?? []) as unknown as CallRow[]).map((r) => ({
    id: r.id,
    kind: 'call',
    at: r.started_at ?? r.created_at,
    phone: r.phone,
    message: r.message,
    status: r.status,
    detail: r.result_detail,
    lead: r.inspection_leads,
    notBefore: r.not_before,
    durationSec: r.duration_sec,
    sipCode: r.sip_code,
    audioPath: r.audio_path,
  }))

  return [...sms, ...calls].sort((a, b) => Date.parse(b.at) - Date.parse(a.at)).slice(0, opts.limit)
}

/** Ссылка на аудио звонка на 10 минут. */
export async function getCallAudioUrl(path: string): Promise<string> {
  const supabase = createClient()
  const { data, error } = await supabase.storage.from('call-audio').createSignedUrl(path, 600)
  if (error) throw new Error(error.message)
  return data.signedUrl
}
